import React, { useState, useEffect } from 'react'
import { Input } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../../EditorContextProvider'
import {
  validateHideIfExpression,
  getValidationMessage,
  getExpressionExamples,
} from '../../../FormRenderer/utils/validateHideIfExpression'

export default function LogicEditElement({
  currentElementState,
  setCurrentElementState,
}: {
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
}) {
  const { setDisableSaveEdit } = useUiFromSpecContext()
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean
    errors: string[]
    warnings?: string[]
  } | null>(null)
  const [showExamples, setShowExamples] = useState(false)

  const currentExpression = currentElementState?.['logic']?.expression || ''

  // Validate expression whenever it changes
  useEffect(() => {
    if (currentExpression.trim()) {
      const result = validateHideIfExpression(currentExpression)
      setValidationResult(result)
    } else {
      setValidationResult(null)
    }
  }, [currentExpression])

  useEffect(() => {
    setDisableSaveEdit?.((validationResult && !validationResult.isValid) ?? false)
  }, [validationResult, setDisableSaveEdit])

  if (!currentElementState) return null
  if (currentElementState.ui_type === 'MAIN_DIV') return null

  return (
    <div className="p-3 py-2 rounded-sm">
      <div className="flex justify-between items-center mb-2">
        <label className="font-semibold">Hide If</label>
        <button
          type="button"
          onClick={() => setShowExamples(!showExamples)}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {showExamples ? 'Hide Examples' : 'Show Examples'}
        </button>
      </div>

      <Input
        className={`w-full ${
          validationResult
            ? validationResult.isValid
              ? 'border-green-300 focus:border-green-500'
              : 'border-red-300 focus:border-red-500'
            : ''
        }`}
        size="sm"
        rounded="lg"
        type="text"
        placeholder="e.g., user_role == 'Admin' && status != 'inactive'"
        value={currentExpression}
        onChange={(e) => {
          setCurrentElementState((prevState: any) => {
            return {
              ...prevState,
              ['logic']: {
                expression: e.target.value,
              },
            }
          })
        }}
      />

      {/* Validation Messages */}
      {validationResult && (
        <div className="mt-2">
          {validationResult.isValid ? (
            <div className="flex items-center text-green-600 text-xs">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Valid expression
            </div>
          ) : (
            <div className="text-red-600 text-xs">
              <div className="flex items-start">
                <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>{getValidationMessage(validationResult)}</div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {validationResult.warnings && validationResult.warnings.length > 0 && (
            <div className="text-yellow-600 text-xs mt-1">
              <div className="flex items-start">
                <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>{validationResult.warnings.join('; ')}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Examples */}
      {showExamples && (
        <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs">
          <div className="font-medium text-gray-700 mb-2">Valid expression examples:</div>
          <div className="space-y-1">
            {getExpressionExamples().map((example, index) => (
              <div key={index} className="text-gray-600 font-mono">
                {example}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
